import { useEffect, useLayoutEffect, useRef, useState } from "react";
import clsx from "clsx";
import { DateTime } from "luxon";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import {
  ArrowDownRight,
  ArrowLeftRight,
  ArrowUpRight,
  Ban,
  Scissors,
  ChevronRight,
  Clock3,
  HelpCircle,
  Hourglass,
  Lock,
  Luggage,
  MoveRight,
  Moon,
  Pin,
  PlaneTakeoff,
  RotateCcw,
  Truck,
  Unlock,
  Wrench,
  X,
} from "lucide-react";
import {
  BAGS_BACK_MIN,
  CANCELLED_PREFIX,
  icaoCodeForWingspan,
  type AirportConfig,
  type CancelledFlight,
  type Disruption,
  type ExtendResource,
  type FlightLeg,
  type OpsEvent,
  type Turn,
} from "@yb/core";
import { api, type Board } from "../lib/api";
import { airlineName, counterFunctionShort, delayMin, delayText, hhmm, label, legStatusText } from "../lib/format";
import { useDebounced } from "../lib/hooks";
import { checkDrop } from "../lib/validate";
import { movedCounters, type ResItem, type ResourceKind } from "./ResourceTimeline";
import { EXTEND_WHAT, extendSpan, extendTarget, extensionText } from "../lib/extend";

const MIN = 60_000;

function eventsFor(board: Board, t: Turn): OpsEvent[] {
  return board.events.filter((e) => (e.arrLegId && e.arrLegId === t.arr?.id) || (e.depLegId && e.depLegId === t.dep?.id) || t.id.startsWith(`T|${e.arrLegId ?? "-"}|${e.depLegId ?? "-"}`));
}

/** What taking a planner's event off the board means, in the words of the event. */
export function undoEventLabel(e: OpsEvent, tz: string): string {
  switch (e.kind) {
    case "tow":
      return `Remove tow to ${e.toStand} (${hhmm(e.at, tz)})`;
    case "mech":
      return "Release maintenance hold";
    case "type":
      return `Undo type correction (${e.aircraftType})`;
    case "link":
      return "Undo pairing made here";
    case "remove":
      return "Put back on the board";
    case "extend":
      return `Remove extension (${EXTEND_WHAT[e.resource].toLowerCase()} to ${hhmm(e.until, tz)})`;
    case "cancel":
      return `Undo cancellation of ${e.label ?? "this flight"}`;
  }
}

/** A flight on the C1 row, by the id its bar carries. */
export function cancelledById(board: Pick<Board, "cancelled">, id: string): CancelledFlight | undefined {
  return id.startsWith(CANCELLED_PREFIX) ? (board.cancelled ?? []).find((c) => c.id === id) : undefined;
}

/** Who cancelled it, in a line: the planner and when, or the feed. */
export function cancelledByText(c: CancelledFlight, tz: string): string {
  if (c.source === "planner") return `Cancelled${c.by ? ` by ${c.by}` : " here"}${c.at ? ` at ${hhmm(c.at, tz)}` : ""}`;
  return c.assumed ? "Dropped out of the airport feed, so taken as cancelled" : "Cancelled in the flight feed";
}

/** The bags question starts at now for the default 45 minutes, or where a planner left it. */
export function defaultBags(now: number, current?: { from: number; to: number } | null): { from: number; minutes: number } {
  if (current) return { from: current.from, minutes: Math.round((current.to - current.from) / MIN) };
  return { from: Math.floor(now / MIN) * MIN, minutes: BAGS_BACK_MIN };
}

/**
 * A pointer event or a scroll inside the menu is the menu being used; one anywhere else
 * means it should go. Scrolling a long submenu fires a scroll event too, which used to
 * close the whole menu before the lower entries could be reached.
 */
export function isOutside(root: Node | null, target: EventTarget | null): boolean {
  return !root || !target || !root.contains(target as Node);
}

/** Keep a floating panel inside the viewport. */
function useClamp(x: number, y: number) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ left: x + 14, top: y + 14 });
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const left = x + 14 + r.width > window.innerWidth - 8 ? Math.max(8, x - r.width - 10) : x + 14;
    const top = y + 14 + r.height > window.innerHeight - 8 ? Math.max(8, y - r.height - 10) : y + 14;
    setPos({ left, top });
  }, [x, y]);
  return { ref, pos };
}

export function HoverCard({ board, config, turnId, x, y }: { board: Board; config: AirportConfig; turnId: string; x: number; y: number }) {
  const t = board.turns.find((z) => z.id === turnId);
  const { ref, pos } = useClamp(x, y);
  const gone = cancelledById(board, turnId);
  if (gone) {
    const tz = board.airport.timezone;
    const leg = gone.leg;
    const belt = config.belts.find((b) => b.id === board.plan.belts[leg.id]?.beltId);
    return (
      <div ref={ref} className="pointer-events-none fixed z-[60] w-80 rounded-xl border border-line bg-panel p-3 text-[12px] shadow-2xl" style={{ left: pos.left, top: pos.top }}>
        <div className="flex items-center gap-2">
          <div className="text-[15px] font-bold text-ink-2 line-through">{leg.flightNumber}</div>
          <span className="rounded bg-bad/15 px-1.5 text-[10px] font-bold text-bad">CANCELLED</span>
        </div>
        <div className="text-ink-2">
          {airlineName(gone.airline, config)} · {leg.direction === "ARR" ? "arrival from" : "departure to"} {leg.otherAirport ?? "?"} · due {hhmm(leg.scheduled, tz)}
        </div>
        <div className="mt-2 text-ink-2">{cancelledByText(gone, tz)}</div>
        {gone.note && <div className="text-ink-3">{gone.note}</div>}
        {leg.direction === "DEP" && (
          <div className="mt-2 rounded-lg bg-panel-2 px-2 py-1.5">
            {gone.bags
              ? `Bags back to ${belt?.name ?? "a reclaim belt (none free)"} ${hhmm(gone.bags.from, tz)}–${hhmm(gone.bags.to, tz)}`
              : gone.source === "planner"
                ? "No bags were checked"
                : "No bags sent to a reclaim belt"}
          </div>
        )}
        <div className="mt-2 text-[10px] text-ink-3">Holds no stand, counter or belt. Right-click for actions</div>
      </div>
    );
  }
  if (!t) return null;
  const tz = board.airport.timezone;
  const stand = config.stands.find((s) => s.id === board.plan.stands[t.id]?.standId);
  const type = t.aircraftType ? config.aircraftTypes[t.aircraftType] : undefined;
  const conflicts = board.conflicts.filter((c) => c.turnIds.includes(t.id));
  const counters = t.dep ? board.plan.counters[t.dep.id] : undefined;
  const belt = t.arr ? config.belts.find((b) => b.id === board.plan.belts[t.arr!.id]?.beltId) : undefined;
  const ob = t.dep ? (config.outboundBelts ?? []).find((b) => b.id === board.plan.outbound?.[t.dep!.id]?.beltId) : undefined;
  const pred = board.predictions.find((p) => p.causeTurnId === t.id);
  const events = eventsFor(board, t);
  // Every allocation of this flight a planner has held longer, with who did it and why.
  const extended = (["stand", "counters", "belts", "outbound"] as const).flatMap((r) => {
    const span = extendSpan(board, r, t);
    return span?.ext && span.end > span.usual ? [{ r, text: extensionText(r, span.ext, span.usual, tz) }] : [];
  });
  const legRow = (leg: NonNullable<Turn["arr"]>) => {
    const d = delayMin(leg);
    return (
      <div className="flex items-start gap-2">
        {leg.direction === "ARR" ? <ArrowDownRight className="mt-0.5 size-3.5 text-ink-3" /> : <ArrowUpRight className="mt-0.5 size-3.5 text-ink-3" />}
        <div className="min-w-0 flex-1">
          <div className="flex justify-between gap-2">
            <b>
              {leg.flightNumber} {leg.direction === "ARR" ? "from" : "to"} {leg.otherAirport ?? "?"}
            </b>
            <span className="text-ink-3">{legStatusText(leg)}</span>
          </div>
          <div className="tabular text-ink-2">
            Sched {hhmm(leg.scheduled, tz)}
            {leg.estimated != null && ` · Est ${hhmm(leg.estimated, tz)}`}
            {leg.actual != null && ` · Act ${hhmm(leg.actual, tz)}`}
            {delayText(d) && <span className={clsx("ml-1 font-bold", d! > 0 ? "text-bad" : "text-ok")}>{delayText(d)}</span>}
          </div>
          {(leg.pax || leg.bags || leg.seats) && (
            <div className="text-ink-2">
              {leg.pax ? `${leg.pax} pax` : ""}
              {leg.pax && leg.seats ? ` of ${leg.seats} seats (${Math.round((leg.pax / leg.seats) * 100)}%)` : leg.seats ? `${leg.seats} seats` : ""}
              {(leg.pax || leg.seats) && leg.bags ? " · " : ""}
              {leg.bags ? `${leg.bags} bags` : ""}
              {leg.loadSource && <span className="text-ink-3"> ({leg.loadSource})</span>}
            </div>
          )}
          {leg.screened != null && (
            <div className="text-info">
              {leg.screened} through security{leg.screenedQueued ? `, ${leg.screenedQueued} in line` : ""}
            </div>
          )}
        </div>
      </div>
    );
  };
  return (
    <div
      ref={ref}
      className="pointer-events-none fixed z-[60] w-80 rounded-xl border border-line bg-panel p-3 text-[12px] shadow-2xl"
      style={{ left: pos.left, top: pos.top }}
    >
      <div className="flex items-center gap-2">
        <div className="text-[15px] font-bold">{label(t)}</div>
        {t.ron && (
          <span className="flex items-center gap-0.5 rounded bg-info/15 px-1.5 text-[10px] font-bold text-info">
            <Moon className="size-3" /> Overnight
          </span>
        )}
        {t.sector !== "D" && <span className="rounded bg-info/15 px-1.5 text-[10px] font-bold text-info">{t.sector === "T" ? "Transborder" : "International"}</span>}
      </div>
      <div className="text-ink-2">
        {airlineName(t.airline, config)} · {t.registration ?? "no registration"} · {type?.name ?? t.aircraftType ?? "type unknown"}
        {type && ` · Code ${icaoCodeForWingspan(type.wingspan)}`}
        {t.aircraftTypeInferred && <span className="text-warn"> (assumed)</span>}
      </div>
      <div className="mt-2 flex items-center justify-between rounded-lg bg-panel-2 px-2 py-1.5">
        <div>
          <div className="text-[10px] text-ink-3 uppercase">Stand</div>
          <div className="text-[14px] font-bold">
            {stand?.name ?? "Unallocated"} {stand?.gate && <span className="text-[11px] font-normal text-ink-3">{stand.gate}</span>}
          </div>
        </div>
        <div className="tabular text-right text-ink-2">
          {hhmm(t.start, tz)}–{hhmm(t.end, tz)}
          <div className="text-[10px] text-ink-3">{Math.round((t.end - t.start) / MIN)} min on ground</div>
        </div>
      </div>
      <div className="mt-2 space-y-1.5">
        {t.arr && legRow(t.arr)}
        {t.dep && legRow(t.dep)}
      </div>
      <div className="mt-2 grid grid-cols-3 gap-1 text-[11px]">
        <div>
          <div className="text-ink-3">Check-in</div>
          <div className="font-semibold">
            {counters?.counters
              .map((c) => {
                const n = config.counters.find((x) => x.id === c)?.number;
                const f = counters.labels?.[c] ?? counterFunctionShort(counters.roles?.[c]);
                return f ? `${n} ${f}` : `${n}`;
              })
              .join(", ") || "—"}
          </div>
        </div>
        <div>
          <div className="text-ink-3">Reclaim</div>
          <div className="font-semibold">{belt?.name.replace("Reclaim belt", "Belt") ?? "—"}</div>
        </div>
        <div>
          <div className="text-ink-3">Outbound</div>
          <div className="font-semibold">{ob?.name.replace("Outbound belt", "Belt") ?? "—"}</div>
        </div>
      </div>
      {(t.mech || events.length > 0 || extended.length > 0 || board.plan.stands[t.id]?.locked || t.towedTo) && (
        <div className="mt-2 space-y-0.5 border-t border-line pt-2 text-[11px]">
          {board.plan.stands[t.id]?.locked && (
            <div className="flex items-center gap-1 text-ink-2">
              <Lock className="size-3" /> Locked by a planner
            </div>
          )}
          {t.mech && (
            <div className="flex items-center gap-1 text-bad">
              <Wrench className="size-3" /> Maintenance hold until {hhmm(t.mech.until, tz)}: {t.mech.note}
            </div>
          )}
          {extended.map((x) => (
            <div key={x.r} className="flex items-center gap-1 text-warn">
              <Hourglass className="size-3 shrink-0" /> {x.text}
            </div>
          ))}
          {events
            .filter((e): e is Extract<OpsEvent, { kind: "tow" }> => e.kind === "tow")
            .map((e) => (
              <div key={e.id} className="flex items-center gap-1 text-info">
                <Truck className="size-3" /> Tow to {e.toStand} at {hhmm(e.at, tz)}
                {e.note ? ` (${e.note})` : ""}
              </div>
            ))}
        </div>
      )}
      {conflicts.length > 0 && (
        <div className="mt-2 space-y-0.5 border-t border-line pt-2 text-[11px]">
          {conflicts.map((c) => (
            <div key={c.id} className={c.severity === "hard" ? "text-bad" : "text-warn"}>
              • {c.message}
            </div>
          ))}
        </div>
      )}
      {pred && (
        <div className="mt-1 text-[11px] text-warn">
          <Clock3 className="mr-1 inline size-3" />
          Predicted {pred.causeDelayMin} min late ({Math.round(pred.probability * 100)}% likely)
        </div>
      )}
      <div className="mt-2 text-[10px] text-ink-3">Right-click for actions</div>
    </div>
  );
}

interface MenuItem {
  key: string;
  label: string;
  icon?: React.ReactNode;
  hint?: string;
  tone?: "bad" | "ok" | "warn";
  disabled?: boolean;
  onClick?: () => void;
  children?: MenuItem[];
}

function MenuList({ items, onClose }: { items: MenuItem[]; onClose: () => void }) {
  const [open, setOpen] = useState<string | null>(null);
  return (
    <div className="min-w-56 py-1">
      {items.map((it) =>
        it.key.startsWith("sep") ? (
          <div key={it.key} className="my-1 border-t border-line" />
        ) : (
          <div key={it.key} className="relative" onMouseEnter={() => setOpen(it.children ? it.key : null)}>
            <button
              disabled={it.disabled}
              onClick={() => {
                if (it.children) return setOpen(open === it.key ? null : it.key);
                it.onClick?.();
                onClose();
              }}
              className={clsx(
                "flex w-full items-center gap-2 px-3 py-1.5 text-left text-[12.5px] hover:bg-panel-2 disabled:opacity-40",
                it.tone === "bad" && "text-bad",
                it.tone === "ok" && "text-ok",
                it.tone === "warn" && "text-warn",
              )}
            >
              <span className="flex size-4 items-center justify-center text-ink-3">{it.icon}</span>
              <span className="flex-1">{it.label}</span>
              {it.hint && <span className="max-w-40 truncate text-[11px] text-ink-3">{it.hint}</span>}
              {it.children && <ChevronRight className="size-3.5 text-ink-3" />}
            </button>
            {open === it.key && it.children && (
              <div className="scroll-thin absolute top-0 left-full z-10 -ml-1 max-h-[60vh] overflow-auto rounded-lg border border-line bg-panel shadow-2xl">
                <MenuList items={it.children} onClose={onClose} />
              </div>
            )}
          </div>
        ),
      )}
    </div>
  );
}

export interface MenuActions {
  explain: (turnId: string) => void;
  move: (turnId: string, standId: string | null, locked?: boolean) => void;
  lock: (turnId: string, locked: boolean) => void;
  tow: (turnId: string) => void;
  mech: (turnId: string) => void;
  /** Swap which aircraft operates which departure. */
  swapTails: (turnId: string) => void;
  /** The arrival and the departure are no longer the same aircraft. */
  split: (turnId: string) => void;
  removeEvent: (id: string) => void;
  /**
   * Hold the stand, desks or a belt for this flight until `until`; with no time, ask for
   * one (and a reason) in a dialog.
   */
  extend: (resource: ExtendResource, turnId: string, until?: number) => void;
  /** Cancel one of the turn's flights for real, asking about bags for a departure. */
  cancelFlight: (turnId: string) => void;
  /** Send a cancelled departure's bags to a reclaim belt, or change when and how long. */
  bags: (cancelledId: string) => void;
  disrupt: (d: Disruption) => void;
  /**
   * One change for every flight in a bar. Counters are worked out per flight from the
   * desks it has now, so a merged bar's flights each keep their own other desks.
   */
  setResource: (kind: ResourceKind, legIds: string[], value: string | null | ((current: string[]) => string[]), clear?: boolean) => void;
}

export function ContextMenu({
  board,
  config,
  turnId,
  item,
  kind,
  x,
  y,
  canEdit,
  actions,
  onClose,
}: {
  board: Board;
  config: AirportConfig;
  turnId: string;
  item: ResItem | null;
  kind: "stands" | ResourceKind;
  x: number;
  y: number;
  canEdit: boolean;
  actions: MenuActions;
  onClose: () => void;
}) {
  const { ref, pos } = useClamp(x - 14, y - 14);
  useEffect(() => {
    const close = (e: Event) => {
      if (e instanceof KeyboardEvent && e.key !== "Escape") return;
      if (!(e instanceof KeyboardEvent) && !isOutside(ref.current, e.target)) return;
      onClose();
    };
    window.addEventListener("mousedown", close);
    window.addEventListener("keydown", close);
    window.addEventListener("scroll", close, true);
    return () => {
      window.removeEventListener("mousedown", close);
      window.removeEventListener("keydown", close);
      window.removeEventListener("scroll", close, true);
    };
  }, [onClose, ref]);

  const t = board.turns.find((z) => z.id === turnId);
  const gone = t ? undefined : cancelledById(board, turnId);
  if (!t && !gone) return null;
  const tz = board.airport.timezone;
  const items: MenuItem[] = [];

  // A flight on the C1 row holds nothing, so there is nothing to move: only its undo and its bags.
  if (gone && canEdit) {
    if (gone.source === "planner" && gone.eventId) {
      items.push({ key: "uncancel", label: "Undo cancellation", hint: "back on the board", icon: <RotateCcw className="size-3.5" />, onClick: () => actions.removeEvent(gone.eventId!) });
    }
    if (gone.leg.direction === "DEP" && kind === "stands") {
      items.push({ key: "bags", label: gone.bags ? "Change bags on the reclaim belt…" : "Send bags to a reclaim belt…", icon: <Luggage className="size-3.5" />, onClick: () => actions.bags(gone.id) });
      if (gone.bags && gone.source === "feed" && gone.eventId) {
        items.push({ key: "nobags", label: "No bags after all", icon: <X className="size-3.5" />, tone: "bad", onClick: () => actions.removeEvent(gone.eventId!) });
      }
    }
  }

  /**
   * Extend: for what happens that the AODB never publishes. Quick steps from where it ends
   * now, any time in a dialog, and a way back: a step shorter, or no extension at all.
   */
  const extendItem = (resource: ExtendResource): MenuItem | null => {
    // A cancelled departure's bags are changed from C1, not extended.
    if (!t) return null;
    const target = extendTarget(board, resource, t.id, item?.legIds);
    const span = target && extendSpan(board, resource, target);
    if (!target || !span) return null;
    const what = resource === "stand" ? "time on stand" : EXTEND_WHAT[resource].toLowerCase();
    const steps: MenuItem[] = [15, 30, 60].map((m) => ({
      key: `ext-${m}`,
      label: `+${m} min`,
      hint: `until ${hhmm(span.end + m * MIN, tz)}`,
      onClick: () => actions.extend(resource, target.id, span.end + m * MIN),
    }));
    steps.push({ key: "ext-until", label: "Until a time…", icon: <Clock3 className="size-3.5" />, onClick: () => actions.extend(resource, target.id) });
    if (span.ext) {
      steps.push({ key: "sep-ext", label: "" });
      if (span.end - 15 * MIN > span.usual) {
        steps.push({ key: "ext-less", label: "15 min shorter", hint: `until ${hhmm(span.end - 15 * MIN, tz)}`, onClick: () => actions.extend(resource, target.id, span.end - 15 * MIN) });
      }
      steps.push({
        key: "ext-off",
        label: "Remove extension",
        hint: `back to ${hhmm(span.usual, tz)}`,
        icon: <RotateCcw className="size-3.5" />,
        onClick: () => actions.removeEvent(span.ext!.eventId),
      });
    }
    const flight = resource === "stand" ? label(target) : (resource === "belts" ? target.arr : target.dep)?.flightNumber;
    return {
      key: "extend",
      label: span.ext ? `Change ${flight} ${what} extension` : `Extend ${flight} ${what}`,
      hint: span.ext ? `to ${hhmm(span.end, tz)}` : undefined,
      icon: <Hourglass className="size-3.5" />,
      children: steps,
    };
  };

  if (t) {
    const a = board.plan.stands[t.id];
    const events = eventsFor(board, t);
    const mainLeg = t.dep ?? t.arr!;
    items.push({ key: "why", label: "Why this stand?", icon: <HelpCircle className="size-3.5" />, onClick: () => actions.explain(t.id) });

    if (canEdit && kind === "stands") {
      const standItems: MenuItem[] = config.stands
        .filter((s) => s.active && s.id !== a?.standId)
        .map((s) => {
          const c = checkDrop(t, s.id, board.turns, board.plan.stands, config, board.closures);
          return {
            key: `mv-${s.id}`,
            label: s.name,
            hint: c.reason,
            tone: c.level === "bad" ? ("bad" as const) : c.level === "ok" ? ("ok" as const) : undefined,
            disabled: c.level === "bad",
            onClick: () => actions.move(t.id, s.id),
          };
        });
      items.push(
        a?.locked
          ? { key: "unlock", label: "Unlock", icon: <Unlock className="size-3.5" />, onClick: () => actions.lock(t.id, false) }
          : { key: "lock", label: "Lock on this stand", icon: <Lock className="size-3.5" />, onClick: () => actions.lock(t.id, true) },
        { key: "move", label: "Move to", icon: <MoveRight className="size-3.5" />, children: standItems, disabled: t.segment === "towed" },
        { key: "tow", label: "Tow to another stand…", icon: <Truck className="size-3.5" />, onClick: () => actions.tow(t.id) },
        { key: "mech", label: t.mech ? "Change maintenance hold…" : "Maintenance hold (MX / AOG)…", icon: <Wrench className="size-3.5" />, onClick: () => actions.mech(t.id) },
        ...[extendItem("stand")].filter((x): x is MenuItem => !!x),
        { key: "tailswap", label: "Tail swap…", icon: <ArrowLeftRight className="size-3.5" />, onClick: () => actions.swapTails(t.id), disabled: !t.arr && !t.dep },
        {
          key: "split",
          label: "Split arrival and departure",
          icon: <Scissors className="size-3.5" />,
          disabled: !t.arr || !t.dep,
          onClick: () => actions.split(t.id),
        },
        {
          key: "cancel-flight",
          label: "Cancel flight…",
          icon: <Ban className="size-3.5" />,
          tone: "bad",
          disabled: !t.arr && !t.dep,
          onClick: () => actions.cancelFlight(t.id),
        },
      );
      // Extensions are taken back from their own submenu, on the tab they belong to.
      for (const e of events.filter((x) => x.kind !== "extend")) {
        items.push({
          key: `rm-${e.id}`,
          label: undoEventLabel(e, tz),
          icon: <RotateCcw className="size-3.5" />,
          onClick: () => actions.removeEvent(e.id),
        });
      }
      items.push(
        { key: "sep1", label: "" },
        {
          key: "delay",
          label: `What if ${mainLeg.flightNumber} is late`,
          icon: <Clock3 className="size-3.5" />,
          children: [15, 30, 45, 60, 90, 120].map((m) => ({
            key: `d${m}`,
            label: `+${m} min`,
            hint: hhmm((mainLeg.estimated ?? mainLeg.scheduled) + m * MIN, tz),
            onClick: () => actions.disrupt({ kind: "delay", legId: mainLeg.id, newTime: (mainLeg.estimated ?? mainLeg.scheduled) + m * MIN }),
          })),
        },
        {
          key: "swap",
          label: "What if the aircraft changes",
          icon: <PlaneTakeoff className="size-3.5" />,
          children: Object.values(config.aircraftTypes)
            .filter((x) => x.code !== t.aircraftType)
            .sort((p, q) => p.code.localeCompare(q.code))
            .map((x) => ({ key: `sw-${x.code}`, label: x.code, hint: `${x.name} · ${x.wingspan} m`, onClick: () => actions.disrupt({ kind: "type-change", legId: mainLeg.id, aircraftType: x.code }) })),
        },
        { key: "cancel", label: `What if ${mainLeg.flightNumber} is cancelled`, icon: <Ban className="size-3.5" />, onClick: () => actions.disrupt({ kind: "cancel", legId: mainLeg.id }) },
        { key: "sep2", label: "" },
        { key: "unalloc", label: "Unallocate", icon: <X className="size-3.5" />, tone: "bad", disabled: !a?.standId || t.segment === "towed", onClick: () => actions.move(t.id, null) },
      );
    }
  }

  if (canEdit && kind !== "stands" && item) {
    const rows =
      kind === "counters"
        ? [...config.counters].sort((p, q) => p.number - q.number).map((c) => ({ id: c.id, name: `Counter ${c.number}` }))
        : kind === "belts"
          ? config.belts.map((b) => ({ id: b.id, name: b.name }))
          : (config.outboundBelts ?? []).filter((b) => b.active).map((b) => ({ id: b.id, name: b.name }));
    // The first flight's desks stand for the bar in the lists offered; the change itself is made per flight.
    const current = kind === "counters" ? board.plan.counters[item.legIds[0] ?? ""]?.counters ?? [] : [item.rowId];
    items.push({
      key: "res-move",
      label: kind === "counters" ? `Move ${item.flight} from ${rows.find((r) => r.id === item.rowId)?.name ?? "—"} to` : `Move ${item.flight} to`,
      icon: <MoveRight className="size-3.5" />,
      children: rows
        .filter((r) => !current.includes(r.id))
        .map((r) => ({
          key: `rm-${r.id}`,
          label: r.name,
          onClick: () => actions.setResource(kind, item.legIds, kind === "counters" ? (cur) => movedCounters(cur, item.rowId, r.id) : r.id),
        })),
    });
    if (kind === "counters") {
      const free = [...config.counters].sort((p, q) => p.number - q.number).filter((c) => !current.includes(c.id));
      items.push({
        key: "res-add",
        label: "Add a counter",
        icon: <Pin className="size-3.5" />,
        children: free.map((c) => ({ key: `add-${c.id}`, label: `Counter ${c.number}`, onClick: () => actions.setResource(kind, item.legIds, (cur) => [...cur.filter((x) => x !== c.id), c.id]) })),
      });
      if (current.length > 1) {
        items.push({ key: "res-drop", label: `Remove ${rows.find((r) => r.id === item.rowId)?.name}`, icon: <X className="size-3.5" />, onClick: () => actions.setResource(kind, item.legIds, (cur) => cur.filter((c) => c !== item.rowId)) });
      }
    }
    const ext = extendItem(kind);
    if (ext) items.push(ext);
    if (item.pinned) items.push({ key: "res-auto", label: "Back to automatic", icon: <RotateCcw className="size-3.5" />, onClick: () => actions.setResource(kind, item.legIds, null, true) });
    if (kind !== "counters") items.push({ key: "res-none", label: "Leave unassigned", icon: <X className="size-3.5" />, tone: "bad", onClick: () => actions.setResource(kind, item.legIds, null) });
  }

  return (
    <div ref={ref} className="fixed z-[70] rounded-xl border border-line bg-panel shadow-2xl" style={{ left: pos.left, top: pos.top }} onContextMenu={(e) => e.preventDefault()}>
      <div className="border-b border-line px-3 py-2">
        {t ? (
          <>
            <div className="text-[13px] font-bold">{label(t)}</div>
            <div className="text-[11px] text-ink-3">
              {config.stands.find((s) => s.id === board.plan.stands[t.id]?.standId)?.name ?? "Unallocated"} · {hhmm(t.start, tz)}–{hhmm(t.end, tz)}
              {t.ron && " · overnight"}
            </div>
          </>
        ) : (
          <>
            <div className="text-[13px] font-bold">{gone!.leg.flightNumber} cancelled</div>
            <div className="text-[11px] text-ink-3">
              C1 · due {hhmm(gone!.leg.scheduled, tz)} · {cancelledByText(gone!, tz)}
            </div>
          </>
        )}
      </div>
      <MenuList items={items} onClose={onClose} />
    </div>
  );
}

function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 z-[80] grid place-items-center bg-black/40 p-4" onClick={onClose}>
      <div className="w-full max-w-md rounded-xl border border-line bg-panel shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center border-b border-line px-4 py-3">
          <div className="flex-1 text-[15px] font-bold">{title}</div>
          <button onClick={onClose} className="rounded p-1 text-ink-3 hover:bg-panel-2" aria-label="Close">
            <X className="size-4" />
          </button>
        </div>
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
}

function toLocalInput(ms: number, tz: string) {
  return DateTime.fromMillis(ms, { zone: tz }).toFormat("yyyy-MM-dd'T'HH:mm");
}
function fromLocalInput(v: string, tz: string) {
  return DateTime.fromISO(v, { zone: tz }).toMillis();
}

/**
 * A tail swap: the aircraft that arrived as one flight leaves as another's. Pick the
 * flight to swap departures with, including an aircraft parked with nothing to operate.
 */
export function SwapDialog({
  board,
  config,
  turn,
  onCancel,
  onSubmit,
}: {
  board: Board;
  config: AirportConfig;
  turn: Turn;
  onCancel: () => void;
  onSubmit: (choice: { withTurnId: string } | { newTail: { registration: string; aircraftType: string | null; airline: string | null } }) => void;
}) {
  const tz = board.airport.timezone;
  const [withId, setWithId] = useState("");
  const [search, setSearch] = useState("");
  /** An aircraft that is not on the board at all: a spare, or one out of the depot. */
  const [fresh, setFresh] = useState<{ registration: string; aircraftType: string; airline: string } | null>(null);
  const stand = (id: string) => config.stands.find((x) => x.id === board.plan.stands[id]?.standId)?.name ?? "no stand";

  // Anything else on the apron that has an aircraft or a departure to trade.
  const candidates = board.turns
    .filter((t) => t.id !== turn.id && (t.dep || t.arr))
    .filter((t) => {
      const q = search.trim().toLowerCase();
      if (!q) return true;
      return [t.arr?.flightNumber, t.dep?.flightNumber, t.registration, stand(t.id)].some((v) => v?.toLowerCase().includes(q));
    })
    .sort((a, b) => a.start - b.start);

  const chosen = board.turns.find((t) => t.id === withId);
  return (
    <Modal title={`Tail swap: ${label(turn)}`} onClose={onCancel}>
      <form
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (fresh) {
            if (fresh.registration.trim().length < 3) return;
            onSubmit({
              newTail: {
                registration: fresh.registration.trim().toUpperCase(),
                aircraftType: fresh.aircraftType || null,
                airline: fresh.airline || null,
              },
            });
            return;
          }
          if (withId) onSubmit({ withTurnId: withId });
        }}
      >
        <div className="text-[12px] text-ink-2">
          {turn.registration ?? "This aircraft"} is on {stand(turn.id)}
          {turn.dep ? ` and due out as ${turn.dep.flightNumber} at ${hhmm(turn.dep.estimated ?? turn.dep.scheduled, tz)}` : " with nothing to operate"}. Each aircraft keeps its
          stand and takes the other&apos;s departure.
        </div>
        <input
          className="w-full rounded-md border border-line bg-panel px-2 py-1.5 text-[13px]"
          placeholder="Find a flight, registration or stand"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div className="scroll-thin max-h-64 space-y-1 overflow-auto">
          {candidates.map((t) => (
            <button
              type="button"
              key={t.id}
              onClick={() => setWithId(t.id)}
              className={clsx(
                "flex w-full items-baseline gap-2 rounded-lg border px-2.5 py-1.5 text-left text-[12.5px]",
                withId === t.id ? "border-info bg-panel-2" : "border-line hover:border-ink-3",
              )}
            >
              <span className="font-semibold">{label(t)}</span>
              <span className="text-ink-3">{t.registration ?? "no tail"}</span>
              <span className="ml-auto text-ink-3">
                {stand(t.id)} · {t.dep ? `out ${hhmm(t.dep.estimated ?? t.dep.scheduled, tz)}` : "parked"}
              </span>
            </button>
          ))}
          {candidates.length === 0 && <div className="text-[12.5px] text-ink-3">Nothing else on the apron matches that.</div>}
        </div>

        {/* An aircraft that is nowhere on the board: a spare, or one coming out of the depot. */}
        {!fresh ? (
          <button
            type="button"
            className="w-full rounded-lg border border-dashed border-line px-2.5 py-2 text-[12.5px] text-ink-2 hover:border-ink-3"
            onClick={() => {
              setWithId("");
              setFresh({ registration: "", aircraftType: turn.aircraftType ?? "", airline: turn.airline ?? "" });
            }}
          >
            + Bring in a tail that is not on the board
          </button>
        ) : (
          <div className="space-y-2 rounded-lg border border-info p-2.5">
            <div className="flex items-center justify-between text-[12px] font-semibold">
              A tail from the depot
              <button type="button" className="text-[11.5px] font-normal text-ink-3 hover:text-ink-2" onClick={() => setFresh(null)}>
                Cancel
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              <input
                autoFocus
                className="w-32 rounded-md border border-line bg-panel px-2 py-1.5 text-[13px] uppercase"
                placeholder="C-FABC"
                value={fresh.registration}
                onChange={(e) => setFresh({ ...fresh, registration: e.target.value })}
              />
              <select
                className="w-28 rounded-md border border-line bg-panel px-2 py-1.5 text-[13px]"
                value={fresh.aircraftType}
                onChange={(e) => setFresh({ ...fresh, aircraftType: e.target.value })}
              >
                <option value="">Type</option>
                {Object.keys(config.aircraftTypes)
                  .sort()
                  .map((code) => (
                    <option key={code} value={code}>
                      {code}
                    </option>
                  ))}
              </select>
              <select
                className="w-36 rounded-md border border-line bg-panel px-2 py-1.5 text-[13px]"
                value={fresh.airline}
                onChange={(e) => setFresh({ ...fresh, airline: e.target.value })}
              >
                <option value="">Airline</option>
                {Object.values(config.airlines).map((a) => (
                  <option key={a.code} value={a.code}>
                    {a.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="text-[11.5px] text-ink-3">
              It is added to storage and put on a stand, then takes {turn.dep?.flightNumber ?? "this departure"}. The aircraft it replaces stays parked until you give it
              something to do.
            </div>
          </div>
        )}

        {chosen && !fresh && (
          <div className="rounded-lg bg-panel-2 p-2 text-[12px] text-ink-2">
            After the swap: {turn.registration ?? label(turn)} operates {chosen.dep?.flightNumber ?? "nothing"}, and {chosen.registration ?? label(chosen)} operates{" "}
            {turn.dep?.flightNumber ?? "nothing"}.
          </div>
        )}
        {fresh && fresh.registration.trim().length >= 3 && (
          <div className="rounded-lg bg-panel-2 p-2 text-[12px] text-ink-2">
            After the swap: {fresh.registration.trim().toUpperCase()} operates {turn.dep?.flightNumber ?? "nothing"}, and {turn.registration ?? label(turn)} stays on{" "}
            {stand(turn.id)}.
          </div>
        )}
        <div className="flex justify-end gap-2">
          <button type="button" className="rounded-lg border border-line px-3 py-1.5 text-[12.5px] font-semibold" onClick={onCancel}>
            Cancel
          </button>
          <button
            disabled={fresh ? fresh.registration.trim().length < 3 : !withId}
            className="rounded-lg bg-ink px-3 py-1.5 text-[12.5px] font-bold text-panel disabled:opacity-60"
          >
            {fresh ? "Bring it in and swap" : "Swap tails"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

/** When the dialog first proposes the tow: well into the stay, and not before anyone could get there. */
export function defaultTowAt(turn: Turn, now: number): number {
  return Math.min(turn.end - 10 * MIN, Math.max(turn.start + (turn.arr ? 30 : 5) * MIN, now + 10 * MIN));
}

/** The query the dialog's text preview lives under, so a test can seed it. */
export const towPreviewKey = (date: string, turnId: string, at: number, toStand: string, note: string) => ["tow-preview", date, turnId, at, toStand, note] as const;

export function TowDialog({
  board,
  config,
  turn,
  now,
  onCancel,
  onSubmit,
  onSettings,
}: {
  board: Board;
  config: AirportConfig;
  turn: Turn;
  now: number;
  onCancel: () => void;
  onSubmit: (at: number, toStand: string, note: string) => void;
  /** Opens Settings on the tow crews, when nobody is set up to be texted. */
  onSettings?: () => void;
}) {
  const tz = board.airport.timezone;
  const current = board.plan.stands[turn.id]?.standId;
  const [at, setAt] = useState(toLocalInput(defaultTowAt(turn, now), tz));
  const [stand, setStand] = useState(config.stands.find((s) => s.active && s.id !== current)?.id ?? "");
  const [note, setNote] = useState(turn.ron ? "Overnight parking" : "");
  const atMs = fromLocalInput(at, tz);
  const valid = atMs > turn.start && atMs < turn.end && !!stand;
  // The text is previewed as the planner types, once the fields have held still for a
  // moment. One string rather than an object, so an unchanged input compares equal.
  const settled = JSON.parse(useDebounced(JSON.stringify({ at: atMs, stand, note: note.trim() }), 300)) as { at: number; stand: string; note: string };
  const preview = useQuery({
    queryKey: towPreviewKey(board.date, turn.id, settled.at, settled.stand, settled.note),
    queryFn: () => api.towPreview(board.date, turn.id, settled.at, settled.stand, settled.note || undefined),
    enabled: valid,
    placeholderData: keepPreviousData,
  });
  const p = preview.data;
  const airline = airlineName(turn.airline, config);
  return (
    <Modal title={`Tow ${label(turn)}`} onClose={onCancel}>
      <form className="space-y-3" onSubmit={(e) => (e.preventDefault(), valid && onSubmit(atMs, stand, note))}>
        <div className="text-[12px] text-ink-2">
          On the ground {hhmm(turn.start, tz)}–{hhmm(turn.end, tz)}
          {turn.deiceMin ? `, incl. ${turn.deiceMin} min de-icing,` : ""} on {current ?? "no stand"}. From the tow time onwards the aircraft is shown on the new stand. A stand
          marked manual only is never chosen by the app, which is what makes it free for tows; one out of service takes nothing.
        </div>
        <label className="block text-[12px] font-semibold">
          Tow at
          <input type="datetime-local" className="mt-1 w-full rounded-md border border-line bg-panel px-2 py-1.5 text-[13px]" value={at} onChange={(e) => setAt(e.target.value)} />
        </label>
        <label className="block text-[12px] font-semibold">
          To stand
          <select className="mt-1 w-full rounded-md border border-line bg-panel px-2 py-1.5 text-[13px]" value={stand} onChange={(e) => setStand(e.target.value)}>
            {config.stands
              .filter((s) => s.id !== current && s.active)
              .map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                  {s.autoAllocate === false ? " (manual only)" : ""}
                </option>
              ))}
          </select>
        </label>
        <label className="block text-[12px] font-semibold">
          Note
          <input className="mt-1 w-full rounded-md border border-line bg-panel px-2 py-1.5 text-[13px]" value={note} onChange={(e) => setNote(e.target.value)} maxLength={200} placeholder="Optional" />
        </label>
        {!valid && <div className="text-[12px] text-bad">The tow time must be while the aircraft is on the ground.</div>}
        {valid && p && (
          <div className="space-y-2 rounded-lg bg-panel-2 p-2.5 text-[12px]">
            {p.crew.length > 0 ? (
              <>
                <div className="text-ink-2">
                  Text goes to: <b>{p.crew.map((c) => c.name).join(", ")}</b>
                </div>
                <pre className="rounded-md border border-line bg-panel px-2 py-1.5 font-mono text-[11.5px] whitespace-pre-wrap text-ink">{p.text}</pre>
              </>
            ) : (
              <div className="flex flex-wrap items-center gap-2 text-ink-2">
                <span>
                  Nobody is set up to be texted for {airline} tows. Add tow crews in Settings → Texting & auto-fix.
                </span>
                {onSettings && (
                  <button type="button" onClick={onSettings} className="rounded-md border border-line px-2 py-0.5 text-[11.5px] font-semibold hover:border-ink-3">
                    Open Settings
                  </button>
                )}
              </div>
            )}
            {p.delivery === "simulated" && <div className="text-ink-3">Texting is simulated until Twilio is set up (Settings → Texting & auto-fix).</div>}
          </div>
        )}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onCancel} className="rounded-lg border border-line px-3 py-2 text-[13px] font-semibold">
            Cancel
          </button>
          <button disabled={!valid} className="rounded-lg bg-ink px-3 py-2 text-[13px] font-bold text-panel disabled:opacity-50">
            {p && p.crew.length > 0 ? `Tow and text ${p.crew.length}` : "Tow"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

export function MechDialog({ board, turn, onCancel, onSubmit }: { board: Board; turn: Turn; onCancel: () => void; onSubmit: (until: number, note: string) => void }) {
  const tz = board.airport.timezone;
  const [until, setUntil] = useState(toLocalInput(turn.mech?.until ?? turn.end + 60 * MIN, tz));
  const [note, setNote] = useState(turn.mech?.note ?? "Maintenance");
  const untilMs = fromLocalInput(until, tz);
  return (
    <Modal title={`Maintenance hold: ${label(turn)}`} onClose={onCancel}>
      <form className="space-y-3" onSubmit={(e) => (e.preventDefault(), note.trim() && onSubmit(untilMs, note.trim()))}>
        <div className="text-[12px] text-ink-2">The aircraft stays on its stand until the time below. Anything planned after it on the stand will show as a conflict.</div>
        <label className="block text-[12px] font-semibold">
          On stand until
          <input type="datetime-local" className="mt-1 w-full rounded-md border border-line bg-panel px-2 py-1.5 text-[13px]" value={until} onChange={(e) => setUntil(e.target.value)} />
        </label>
        <label className="block text-[12px] font-semibold">
          Reason
          <input className="mt-1 w-full rounded-md border border-line bg-panel px-2 py-1.5 text-[13px]" value={note} onChange={(e) => setNote(e.target.value)} maxLength={200} />
        </label>
        <div className="flex flex-wrap gap-1">
          {["Maintenance", "AOG", "Mechanical delay", "De-icing", "Crew", "Catering", "Security"].map((r) => (
            <button type="button" key={r} onClick={() => setNote(r)} className="rounded-full border border-line px-2 py-0.5 text-[11px] hover:border-ink-3">
              {r}
            </button>
          ))}
        </div>
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onCancel} className="rounded-lg border border-line px-3 py-2 text-[13px] font-semibold">
            Cancel
          </button>
          <button className="rounded-lg bg-bad px-3 py-2 text-[13px] font-bold text-white">Hold aircraft</button>
        </div>
      </form>
    </Modal>
  );
}

/** Reasons a duty manager gives for holding something longer, one click each. */
export const EXTEND_REASONS = ["Crew late", "Passenger issue", "Bags delayed", "Equipment fault", "Weather", "Security"];

/**
 * Extend an allocation to a chosen time, with an optional reason. Opened from "Until a
 * time…" in the right-click menu on any of the four tabs; the quick +15 / +30 / +60 steps
 * there need no dialog.
 */
export function ExtendDialog({
  board,
  turn,
  resource,
  onCancel,
  onSubmit,
  onRemove,
}: {
  board: Board;
  turn: Turn;
  resource: ExtendResource;
  onCancel: () => void;
  onSubmit: (until: number, note: string) => void;
  /** Takes the extension back, when there is one. */
  onRemove: (eventId: string) => void;
}) {
  const tz = board.airport.timezone;
  const span = extendSpan(board, resource, turn);
  const [until, setUntil] = useState(toLocalInput(span ? (span.ext ? span.end : span.usual + 30 * MIN) : board.now, tz));
  const [note, setNote] = useState(span?.ext?.note ?? "");
  if (!span) return null;
  const untilMs = fromLocalInput(until, tz);
  const valid = untilMs > span.usual && untilMs <= span.usual + 24 * 60 * MIN;
  const flight = resource === "stand" ? label(turn) : ((resource === "belts" ? turn.arr : turn.dep)?.flightNumber ?? label(turn));
  const what = resource === "stand" ? "on its stand" : EXTEND_WHAT[resource].toLowerCase();
  const mins = Math.round((untilMs - span.usual) / MIN);
  return (
    <Modal title={`Extend ${flight}: ${EXTEND_WHAT[resource].toLowerCase()}`} onClose={onCancel}>
      <form className="space-y-3" onSubmit={(e) => (e.preventDefault(), valid && onSubmit(untilMs, note.trim()))}>
        <div className="text-[12px] text-ink-2">
          {resource === "stand"
            ? `The aircraft is planned to leave its stand at ${hhmm(span.usual, tz)}. Extended, it stays there until the time below: nothing else is planned onto the stand meanwhile, and anything already put there by hand shows as a conflict.`
            : `${flight} ${what} normally ends at ${hhmm(span.usual, tz)}. Extended, it is kept for this flight until the time below: no other flight is planned onto it meanwhile, and one already put there by hand shows as a conflict.`}
        </div>
        <div className="flex flex-wrap gap-1">
          {[15, 30, 60, 90].map((m) => (
            <button
              type="button"
              key={m}
              onClick={() => setUntil(toLocalInput(span.usual + m * MIN, tz))}
              className={clsx("rounded-full border px-2 py-0.5 text-[11px]", mins === m ? "border-info bg-panel-2" : "border-line hover:border-ink-3")}
            >
              +{m} min
            </button>
          ))}
        </div>
        <label className="block text-[12px] font-semibold">
          {resource === "stand" ? "On stand until" : "Until"}
          <input type="datetime-local" className="mt-1 w-full rounded-md border border-line bg-panel px-2 py-1.5 text-[13px]" value={until} onChange={(e) => setUntil(e.target.value)} />
        </label>
        <label className="block text-[12px] font-semibold">
          Reason
          <input className="mt-1 w-full rounded-md border border-line bg-panel px-2 py-1.5 text-[13px]" value={note} onChange={(e) => setNote(e.target.value)} maxLength={200} placeholder="Optional" />
        </label>
        <div className="flex flex-wrap gap-1">
          {EXTEND_REASONS.map((r) => (
            <button type="button" key={r} onClick={() => setNote(r)} className="rounded-full border border-line px-2 py-0.5 text-[11px] hover:border-ink-3">
              {r}
            </button>
          ))}
        </div>
        {valid ? (
          <div className="text-[12px] text-ink-2">
            {mins} min longer than planned, until {hhmm(untilMs, tz)}.
          </div>
        ) : (
          <div className="text-[12px] text-bad">Choose a time after {hhmm(span.usual, tz)} and within 24 hours of it.</div>
        )}
        <div className="flex items-center justify-end gap-2">
          {span.ext && (
            <button type="button" onClick={() => onRemove(span.ext!.eventId)} className="mr-auto rounded-lg border border-line px-3 py-2 text-[13px] font-semibold text-bad">
              Remove extension
            </button>
          )}
          <button type="button" onClick={onCancel} className="rounded-lg border border-line px-3 py-2 text-[13px] font-semibold">
            Cancel
          </button>
          <button disabled={!valid} className="rounded-lg bg-ink px-3 py-2 text-[13px] font-bold text-panel disabled:opacity-50">
            Extend
          </button>
        </div>
      </form>
    </Modal>
  );
}

/** The flights of a turn a planner can cancel: the departure first, as that is the usual call. */
export function cancellableLegs(turn: Turn): FlightLeg[] {
  return [turn.dep, turn.arr].filter((l): l is FlightLeg => !!l && l.status !== "cancelled");
}

/**
 * Cancel a flight for real, or deal with a cancelled departure's bags.
 *
 * Given a turn: pick the flight (a turn is an arrival and a departure, and only one of
 * them may be off), and for a departure answer "Were bags checked?". Yes puts the bags on
 * a reclaim belt from now for 45 minutes, both of which can be changed here and later.
 * Given a flight already on the C1 row: just the bags question.
 */
export function CancelDialog({
  board,
  turn,
  cancelled,
  now,
  onCancel,
  onSubmit,
}: {
  board: Board;
  turn?: Turn;
  cancelled?: CancelledFlight;
  now: number;
  onCancel: () => void;
  onSubmit: (legId: string, bags: { from: number; minutes: number } | null, note: string) => void;
}) {
  const tz = board.airport.timezone;
  const legs = turn ? cancellableLegs(turn) : cancelled ? [cancelled.leg] : [];
  const [legId, setLegId] = useState(legs[0]?.id ?? "");
  const leg = legs.find((l) => l.id === legId);
  const start = defaultBags(now, cancelled?.bags);
  /** Null until answered: the question is asked, not assumed. */
  const [checked, setChecked] = useState<boolean | null>(cancelled ? true : null);
  const [from, setFrom] = useState(toLocalInput(start.from, tz));
  const [minutes, setMinutes] = useState(String(start.minutes));
  const [note, setNote] = useState("");
  const isDep = leg?.direction === "DEP";
  const mins = Number(minutes);
  const fromMs = fromLocalInput(from, tz);
  const bagsValid = !checked || (Number.isFinite(fromMs) && Number.isInteger(mins) && mins >= 5 && mins <= 600);
  const valid = !!leg && (!isDep || checked !== null) && bagsValid;
  const title = cancelled ? `Bags of ${cancelled.leg.flightNumber}` : `Cancel ${turn ? label(turn) : "flight"}`;
  return (
    <Modal title={title} onClose={onCancel}>
      <form
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (!valid || !leg) return;
          onSubmit(leg.id, isDep && checked ? { from: fromMs, minutes: mins } : null, note.trim());
        }}
      >
        {!cancelled && (
          <div className="text-[12px] text-ink-2">
            The flight moves to the C1 row, at its scheduled time. Whatever it held - its stand, check-in counters and outbound belt - is free straight away. Undo it from the
            C1 row if it operates after all.
          </div>
        )}
        {legs.length > 1 && (
          <div className="space-y-1">
            <div className="text-[12px] font-semibold">Which flight is cancelled?</div>
            {legs.map((l) => (
              <label key={l.id} className={clsx("flex items-center gap-2 rounded-lg border px-2.5 py-1.5 text-[12.5px]", legId === l.id ? "border-info bg-panel-2" : "border-line")}>
                <input type="radio" name="cancel-leg" checked={legId === l.id} onChange={() => setLegId(l.id)} />
                <span className="font-semibold">{l.flightNumber}</span>
                <span className="text-ink-3">
                  {l.direction === "ARR" ? `arriving from ${l.otherAirport ?? "?"}` : `departing to ${l.otherAirport ?? "?"}`} · {hhmm(l.scheduled, tz)}
                </span>
              </label>
            ))}
            <div className="text-[11.5px] text-ink-3">The other flight stays on the board on its own.</div>
          </div>
        )}
        {isDep && (
          <div className="space-y-2">
            <div className="text-[12px] font-semibold">Were bags checked?</div>
            <div className="flex gap-2">
              {([
                [true, "Yes, bring them back"],
                [false, "No"],
              ] as const).map(([v, text]) => (
                <button
                  type="button"
                  key={String(v)}
                  onClick={() => setChecked(v)}
                  className={clsx("rounded-lg border px-3 py-1.5 text-[12.5px] font-semibold", checked === v ? "border-info bg-info/10 text-info" : "border-line hover:border-ink-3")}
                >
                  {text}
                </button>
              ))}
            </div>
            {checked && (
              <div className="space-y-2 rounded-lg bg-panel-2 p-2.5">
                <div className="text-[12px] text-ink-2">
                  The bags go to a reclaim belt for the passengers to collect, planned like an arriving flight&apos;s. Change the belt from the Reclaim belts tab.
                </div>
                <div className="flex flex-wrap gap-2">
                  <label className="block flex-1 text-[12px] font-semibold">
                    On the belt from
                    <input type="datetime-local" className="mt-1 w-full rounded-md border border-line bg-panel px-2 py-1.5 text-[13px]" value={from} onChange={(e) => setFrom(e.target.value)} />
                  </label>
                  <label className="block w-28 text-[12px] font-semibold">
                    For (min)
                    <input type="number" min={5} max={600} step={5} className="mt-1 w-full rounded-md border border-line bg-panel px-2 py-1.5 text-[13px]" value={minutes} onChange={(e) => setMinutes(e.target.value)} />
                  </label>
                </div>
                {!bagsValid && <div className="text-[12px] text-bad">Give a time, and between 5 and 600 minutes on the belt.</div>}
              </div>
            )}
          </div>
        )}
        {!cancelled && (
          <label className="block text-[12px] font-semibold">
            Note
            <input className="mt-1 w-full rounded-md border border-line bg-panel px-2 py-1.5 text-[13px]" value={note} onChange={(e) => setNote(e.target.value)} maxLength={200} placeholder="Optional, e.g. crew out of hours" />
          </label>
        )}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onCancel} className="rounded-lg border border-line px-3 py-2 text-[13px] font-semibold">
            {cancelled ? "Close" : "Keep the flight"}
          </button>
          <button disabled={!valid} className={clsx("rounded-lg px-3 py-2 text-[13px] font-bold disabled:opacity-50", cancelled ? "bg-ink text-panel" : "bg-bad text-white")}>
            {cancelled ? "Save bags" : `Cancel ${leg?.flightNumber ?? "flight"}`}
          </button>
        </div>
      </form>
    </Modal>
  );
}
